import { defineComponent as _defineComponent } from 'vue'
import CheckboxInput from "@/components/ui/input/CheckboxInput.vue";
  import InputSearch from "@/components/ui/input/InputSearch.vue";
  import Modal from "@/components/ui/Modal.vue";
  import { useStore } from "vuex";
  import {
    computed,
    onMounted,
    ref,
    watch,
  } from "vue";
  import { debounce } from "@/helpers/debounce";
  import plural from "@/features/plural";
  import { heightAnimation } from "@/helpers/heightAnimation";
  import { StageInterface } from "@/types/StageInterface";
  import { LotStatus } from "@/enums/LotStatus";
  import { LotBasicInfoInterface } from "@/types/LotBasicInfoInterface";
  import { formatPrice } from "@/helpers/transformation/price";
  import { useToast } from "vue-toastification";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'ShareLotsModal',
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    leadIds: {
      type: Array,
      default: () => {
        return [];
      },
    },
    fileData: {
      type: Object,
      default: () => {
        return {};
      },
    },
    type: {
      type: String,
      default: "leads",
    },
    estateId: {
      type: String,
    },
  },
  emits: ["close"],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();

  const toast = useToast();
  const store = useStore();
  const emits = __emit;
  const props = __props;
  const loadingLots = ref(true);
  const searchQuery = ref("");

  const stages = ref<StageInterface[]>([]);
  const lots = ref<LotBasicInfoInterface[]>([]);
  const activeStagesIndex = ref<boolean[]>([]);
  const tableRefs = ref([]);
  const groupedLots = computed(() => {
    const grouped: Array<LotBasicInfoInterface[]> = [];

    stages.value.forEach((stage: StageInterface) => {
      grouped.push(
        lots.value.filter(
          (lot: LotBasicInfoInterface) => lot.stageId === stage._id
        )
      );
    });
    return grouped;
  });
  const groupedLotCheckboxList = ref([]);
  const groupedSelectAllCheckboxList = ref([]);
  const selectedLots = computed(() => {
    return groupedLotCheckboxList.value.reduce(
      (previousValue: any[], currentValue: any[]) => {
        return previousValue.concat(
          currentValue.filter((lot: any) => lot.selected).map((item) => item.id)
        );
      },
      []
    );
  });

  onMounted(() => {
    getStages();
  });

  watch(
    () => searchQuery.value,
    () => {
      loadingLots.value = true;
      debounceSearch();
      unselectCheckboxes();
    }
  );

  const getStages = () => {
    store
      .dispatch("stage/getStages", {
        estateId: props.estateId,
      })
      .then((res) => {
        stages.value = res.data;
        getLots();
        return res.data;
      });
  };

  const debounceSearch = debounce(getStages);

  const getLots = () => {
    groupedLotCheckboxList.value = [];
    groupedSelectAllCheckboxList.value = [];
    store
      .dispatch("lot/getEstateLots", {
        estateId: props.estateId,
        params: {
          query: searchQuery.value,
          status: props.type === "leads" ? LotStatus.AVAILABLE : null,
        },
      })
      .then((res) => {
        lots.value = res.data;
        groupedLots.value.forEach((stage: LotBasicInfoInterface[], index) => {
          groupedLotCheckboxList.value.push(
            stage.map((lot: LotBasicInfoInterface) => {
              return {
                id: lot.lotId,
                selected: false,
                documents: lot.documents,
              };
            })
          );
          groupedSelectAllCheckboxList.value.push({
            stageIndex: index,
            selected: false,
            selectedLots: 0,
          });
          activeStagesIndex.value.push(true);
          tableRefs.value.forEach((section) => {
            heightAnimation(section, "opened", true);
          });
        });
      })
      .finally(() => {
        loadingLots.value = false;
      });
  };
  const selectLot = (
    isSelected: boolean,
    stageIndex: number,
    lotId: string
  ) => {
    const selectedLotIndex = groupedLotCheckboxList.value[stageIndex].findIndex(
      (el) => el.id === lotId
    );

    groupedLotCheckboxList.value[stageIndex][selectedLotIndex].selected =
      isSelected;
    groupedSelectAllCheckboxList.value[stageIndex].selectedLots += isSelected
      ? 1
      : -1;
    groupedSelectAllCheckboxList.value[stageIndex].selected =
      groupedSelectAllCheckboxList.value[stageIndex].selectedLots > 0;
  };
  const selectAll = (isSelected: boolean, stageIndex: number) => {
    let lotsId = [] as unknown[];

    if (
      groupedLotCheckboxList.value[stageIndex] &&
      groupedLotCheckboxList.value[stageIndex].length
    ) {
      lotsId = groupedLotCheckboxList.value[stageIndex]
        .map((item: any) => item)
        .map((lot: any) => {
          if (!isDocumentExist(lot.documents)) {
            return lot.id;
          }

          return undefined;
        })
        .filter((id: string | undefined) => id);
    }

    if (groupedSelectAllCheckboxList.value[stageIndex]) {
      groupedSelectAllCheckboxList.value[stageIndex].selected = lotsId.length
        ? isSelected
        : false;
      groupedSelectAllCheckboxList.value[stageIndex].selectedLots = isSelected
        ? lotsId.length
        : 0;
    }

    if (groupedLotCheckboxList.value[stageIndex]) {
      groupedLotCheckboxList.value[stageIndex].forEach((item) => {
        if (lotsId.includes(item.id)) {
          item.selected = isSelected;
        }
      });
    }
  };

  function onLotRow(index: number) {
    activeStagesIndex.value[index] = !activeStagesIndex.value[index];
    const section = tableRefs.value[index];

    heightAnimation(section);
  }
  const send = () => {
    if (!selectedLots.value.length) {
      return;
    }

    const action =
      props.type === "leads"
        ? "contact/shareLeadsLinks"
        : "estate/shareDocument";
    const payload =
      props.type === "leads"
        ? {
            leadIds: props.leadIds,
            lotIds: selectedLots.value,
          }
        : {
            fileData: props.fileData,
            lotIds: selectedLots.value,
          };

    store
      .dispatch(action, payload)
      .then(() => {
        toast.success(
          props.type === "leads"
            ? "Emails were successfully sent"
            : "File was successfully shared"
        );
      })
      .catch(() => {
        toast.error("Something went wrong");
      })
      .finally(() => {
        close();
      });
  };
  const close = () => {
    groupedLotCheckboxList.value = [];
    groupedSelectAllCheckboxList.value = [];
    searchQuery.value = "";
    getStages();
    emits("close");
  };

  function unselectCheckboxes() {
    stages.value.forEach((stage: StageInterface, index: number) => {
      selectAll(false, index);
    });
  }
  const isDocumentExist = (lotDocuments: any) => {
    if (lotDocuments) {
      return lotDocuments
        .map((item: any) => item.ETag)
        .includes(props.fileData.ETag);
    }

    return false;
  };

  function getStageCheckedPartial(index: number) {
    const list = groupedSelectAllCheckboxList.value;

    return (
      list[index] &&
      list[index].selectedLots > 0 &&
      list[index].selectedLots !== groupedLotCheckboxList.value[index].length
    );
  }

  function getStageModelValue(index: number) {
    const entry = groupedSelectAllCheckboxList.value[index];

    return entry ? entry.selected : false;
  }

  function isLotRowDisabled(index: number, idx: number) {
    const docs =
      groupedLotCheckboxList.value?.[index]?.[idx]?.documents ?? false;

    return (
      isDocumentExist(docs) ||
      (props.type === "leads" &&
        selectedLots.value.length === 1 &&
        groupedLots.value[index][idx]?.lotId !== selectedLots.value[0])
    );
  }

  function getLotModelValue(index: number, idx: number) {
    return groupedLotCheckboxList.value?.[index]?.[idx]?.selected ?? false;
  }

const __returned__ = { toast, store, emits, props, loadingLots, searchQuery, stages, lots, activeStagesIndex, tableRefs, groupedLots, groupedLotCheckboxList, groupedSelectAllCheckboxList, selectedLots, getStages, debounceSearch, getLots, selectLot, selectAll, onLotRow, send, close, unselectCheckboxes, isDocumentExist, getStageCheckedPartial, getStageModelValue, isLotRowDisabled, getLotModelValue, CheckboxInput, InputSearch, Modal, get useStore() { return useStore }, computed, onMounted, ref, watch, get debounce() { return debounce }, get plural() { return plural }, get heightAnimation() { return heightAnimation }, get StageInterface() { return StageInterface }, get LotStatus() { return LotStatus }, get LotBasicInfoInterface() { return LotBasicInfoInterface }, get formatPrice() { return formatPrice }, get useToast() { return useToast } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})